import { useMemo } from 'react';

import {
  getProfileStateProps,
  ProfileStoreState,
  useProfileStore,
} from '@/shared/store/profile.store';
import { log } from '@/shared/utils/log';

import { useApi } from '../api/hooks/useApi';
import { getBearerToken } from '../api/utils';
import { dpopKeyManager } from '../lib/crypto/dpop';
import { useAuthStore } from '../store/auth.store';

export const useInitProfile = () => {
  const api = useApi();
  const authStore = useAuthStore();
  const profileStore = useProfileStore();

  const initProfileStore = (profile: Partial<ProfileStoreState>) => {
    if (profile.profileType) {
      profileStore.setProfileType(profile.profileType);
    }

    if (profile.encryptionType) {
      profileStore.setEncryptionType(profile.encryptionType);
    }

    if (profile.publicKey) {
      profileStore.setPublicKey(profile.publicKey);
    }

    if (profile.mainUserId) {
      profileStore.setMainUserId(profile.mainUserId);
    }

    if (profile.baseCurrency) {
      profileStore.setBaseCurrency(profile.baseCurrency);
    }

    if (profile.profileId) {
      profileStore.setProfileId(profile.profileId);
    }

    if (profile.hasCompletedOnboarding) {
      profileStore.setHasCompletedOnboarding(profile.hasCompletedOnboarding);
    }

    profileStore.setFullName(profile.firstName ?? undefined, profile.lastName ?? undefined);
    profileStore.setSoweAppendixSuppressCancelConfirmation(
      profile.soweAppendixSuppressCancelConfirmation ?? false,
    );
    profileStore.setOcrSuppressLetsTryConfirmation(profile.ocrSuppressLetsTryConfirmation ?? false);
  };

  const initProfile = async (token: string) => {
    const res = await api.me(token);
    let hasCompletedOnboarding = false;

    // TODO: rework after completely moving to auth service
    // Skip authMe when DPoP key is not yet loaded (e.g. before PIN unlock after page reload)
    if (authStore.accessToken && dpopKeyManager.hasKey()) {
      const resMe = await api.authMe(getBearerToken(authStore.accessToken));
      hasCompletedOnboarding = resMe.data?.hasCompletedOnboarding ?? false;
    }

    const schemaVersion = res?.data?.schemaVersion;
    if (schemaVersion) {
      authStore.setSchemaVersion(schemaVersion);
    }

    const profile = res.data?.profile;

    if (profile) {
      // TODO: rework after fully moving to new auth service
      initProfileStore({
        ...getProfileStateProps(profile),
        hasCompletedOnboarding,
      });
    } else {
      log.error('[useInitProfile] empty profile response');
      return profile;
    }

    return profile;
  };

  return useMemo(() => ({ initProfileStore, initProfile }), [initProfileStore, initProfile]);
};
